import { ExtensionType, GeneratedAnimalName } from '../types/ExtensionCard';
import { GameStage, HandType } from '../types/roomState';
import { formatCount } from './plural';
import {
  BaseMessageDescriptor,
  CardMessageKey,
  GeneratedAnimalNameMessageKey,
  HandTypeMessageKey,
  MessageFormatter,
  MessageValues,
  StageMessageKey,
} from './types';

type GeneralMessageEntry = string | MessageFormatter;
type FormatterValues = Record<string, string | number | boolean | undefined>;

const generalMessagesEn = {
  'app.name': 'Evolution',
  'app.title': ({ version }: FormatterValues) => `Evolution v${version}`,
  'header.leave': 'Leave',
  'header.save': 'Save',
  'header.editLayout': 'Edit Layout',
  'header.reset': 'Reset',
  'header.fullScreen': 'Full Screen',
  'header.admin': 'Admin',
  'header.game': 'Game',
  'header.language.ru': 'RU',
  'header.language.en': 'EN',
  'footer.home': 'Home',
  'footer.rules': 'Game Rules',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.ok': 'OK',
  'common.you': 'You',
  'player.unnamed': 'Unnamed',
  'player.someone': 'Someone',
  'player.another': 'another player',
  'animal.fallback': 'Animal',
  'animal.meta.age': ({ count }: FormatterValues) =>
    formatCount('en', Number(count), { one: 'year', other: 'years' }),
  'animal.meta.cantAttack': "can't attack",
  'animal.meta.poisoned': 'poisoned',
  'animal.meta.hidden': 'hidden',
  'animal.meta.cantEat': "can't eat",
  'animal.meta.protected': 'protected',
  'animal.meta.full': 'full',
  'animal.meta.inBurrow': 'in burrow',
  'animal.meta.kg': ({ count }: FormatterValues) => `${count} kg`,
  'animal.meta.hatchesIn': ({ count }: FormatterValues) =>
    `hatches in ${formatCount('en', Number(count), {
      one: 'turn',
      other: 'turns',
    })}`,
  'animals.youTag': 'You',
  'animals.ready': 'Ready',
  'ready.ready': 'Ready',
  'ready.notReady': 'Not Ready',
  'hud.stage': 'Stage',
  'hud.active': 'Active',
  'hud.remaining': 'Remaining',
  'hud.finalTurn': 'Final Turn',
  'hud.turnsApprox': ({ count }: FormatterValues) =>
    `~${formatCount('en', Number(count), { one: 'turn', other: 'turns' })}`,
  'hud.minutesApprox': ({ count }: FormatterValues) =>
    `~${formatCount('en', Number(count), {
      one: 'minute',
      other: 'minutes',
    })}`,
  'splash.starting': 'Starting',
  'fsloader.waiting': 'Waiting for connection...',
  'fsloader.retry': 'Try again',
  'mobile.title': 'Mobile Version',
  'mobile.description.1': 'This game is not optimized for mobile devices yet.',
  'mobile.description.2': 'Press "OK" to continue to the main interface.',
  'confirm.attackOwnAnimal': 'This is your animal. Are you sure?',
  'name.label': 'Name',
  'name.example': ({ example }: FormatterValues) => `For example, ${example}`,
  'waiting.avatar': 'Avatar',
  'waiting.gameSettings': 'Game Settings',
  'waiting.personalSettings': 'Personal Settings',
  'waiting.startGame': 'Start Game',
  'waiting.players': 'Players:',
  'waiting.youSuffix': '(You)',
  'waiting.linkCopied': 'Link copied!',
  'waiting.modal.settings.title': 'Settings',
  'waiting.modal.settings.initialCards': 'Initial cards per player:',
  'waiting.modal.settings.cardsPerSurvivor': 'Cards per surviving animal:',
  'waiting.modal.settings.totalCards': 'Total cards in deck:',
  'waiting.modal.settings.foodFrom': 'Food from',
  'waiting.modal.settings.foodTo': 'to',
  'waiting.modal.settings.deckSettings': 'Deck Settings',
  'waiting.modal.settings.reset': 'Reset Settings',
  'waiting.modal.settings.selectAll': 'Select All',
  'waiting.modal.settings.card': 'Card',
  'waiting.modal.personal.title': 'Personal Settings',
  'results.player': 'Player',
  'results.score': 'Score',
  'results.additional': 'Additional results:',
  'results.startNewGame': 'Start a New Game',
  'extension.use': 'Use',
  'extension.giftFrom': 'Gift from {playerName}',
  'hand.preview.title': 'Card',
  'hand.preview.body': 'I am a card',
  'start.eyebrow': 'Online survival card game',
  'start.title': 'Build species that can survive, feed, and outlast the table.',
  'start.description':
    'Create animals, evolve traits, compete for food, and punish weak adaptations. The best ecosystem wins.',
  'start.howToStart': 'How to start',
  'start.whatHappensNext': 'What happens next',
  'start.quickRules': 'Quick rules',
  'start.readFullRules': 'Read the full rules',
  'start.rulesNote': 'or jump in and learn from the card reference.',
  'start.form.title': 'Join or create a room',
  'start.form.description':
    'Use the same room code as your friends to join the same match.',
  'start.roomCode': 'Room Code',
  'start.roomExample': ({ example }: FormatterValues) =>
    `For example, ${example}`,
  'start.roomHelp': 'Matching room codes always point to the same room.',
  'start.publicRoom': 'Public Room',
  'start.publicRoomHelp':
    'Public rooms appear in the Open Rooms list so other players can discover them.',
  'start.nameHelp': 'Your display name can be changed again in the lobby.',
  'start.play': 'Play',
  'start.playHint.ready': 'Press Play to enter the lobby for this room.',
  'start.playHint.empty':
    'Enter a room code to create a new room or join an existing one.',
  'start.openRooms.title': 'Join an open public room',
  'start.openRooms.description':
    'Pick a room below if you want to jump into a live lobby.',
  'start.openRooms.players': ({ count }: FormatterValues) =>
    `Players: ${count}`,
  'start.step.1': 'Enter a room code to join friends or create a new table.',
  'start.step.2': 'Choose the name other players will see.',
  'start.step.3': 'Press Play to enter the lobby and get ready for the match.',
  'start.next.1': 'Set your avatar and personal settings in the lobby.',
  'start.next.2':
    'Invite friends with the room link or wait for others to join.',
  'start.next.3': 'Start the game once everyone is ready.',
  'start.rule.1':
    'Food drives every round, so hungry animals are always under pressure.',
  'start.rule.2':
    'Traits help your species feed faster, defend themselves, or become predators.',
  'start.rule.3':
    'The winner is the player whose animals adapt best over time.',
  'rules.pageTitle': 'Game Rules',
  'rules.hero.eyebrow': 'Overview',
  'rules.hero.title': 'Build, Feed, Survive',
  'rules.hero.text':
    'Grow a line of animals, adapt them with traits, and keep more of them alive than everyone else. Strong combinations matter, but so does timing: food is limited, predators are dangerous, and every surviving animal is worth points.',
  'rules.goal.title': 'Goal & Scoring',
  'rules.goal.text':
    'The game ends when the deck runs out and the final extinction phase is resolved. Only surviving animals score.',
  'rules.goal.point.1': 'Each surviving animal is worth 2 points.',
  'rules.goal.point.2': 'Each surviving trait starts at 1 point.',
  'rules.goal.point.3':
    'Traits that change food demand also change their point value by the same amount. For example, Predator and Large are worth 2 total points as traits, while Parasite is worth 3.',
  'rules.cards.title': 'How Cards Work',
  'rules.cards.text':
    'Every evolution card can be used in one of two ways: it can start a new animal, or it can be attached as a trait to an animal already on the table.',
  'rules.cards.point.1':
    'Most traits can only appear once on the same animal. Fat Reserve and Intellect are notable exceptions and may be stacked.',
  'rules.cards.point.2':
    'Paired traits link two animals and live on both sides of that pair.',
  'rules.cards.point.3':
    'Some hostile traits, such as Parasite, Trematode, and Tumor, are played onto enemy animals instead of your own.',
  'rules.cards.point.4':
    'Some traits also conflict with specific other traits, so not every combination is legal.',
  'rules.placing.title': 'Card Placement',
  'rules.placing.text':
    'During Card Placement, players build their board by creating new animals and adding traits. The stage stays open until each player is done and marks themselves ready.',
  'rules.placing.point.1':
    'You can keep placing cards while the placement stage is open.',
  'rules.placing.point.2':
    'A card used to create an animal is removed from your hand and becomes the base of a new species.',
  'rules.placing.point.3':
    'A card used as a trait is attached to an existing animal, or to a legal pair of animals if the trait is paired.',
  'rules.feeding.title': 'Feeding',
  'rules.feeding.text':
    'Feeding is turn-based. The active player resolves one useful feeding action, then priority passes to the next player who is not finished yet.',
  'rules.feeding.point.1':
    'A normal feeding action takes 1 food from the shared food base and places it on one of your animals.',
  'rules.feeding.point.2':
    'A fed animal cannot keep taking normal food, but extra intake can still fill Fat Reserve.',
  'rules.feeding.point.3':
    'Predator attacks replace normal feeding for that action. If the prey cannot defend itself, the predator gains food and other traits may trigger from the kill.',
  'rules.feeding.point.4':
    'Cooperation feeds a paired animal whenever the first animal gets food. Communication is stricter: it only triggers when the first animal ate from the food base, and it spends another food from that same base.',
  'rules.feeding.point.5':
    'Scavenger gains food when another animal is eaten, and stored fat can later be converted into regular food during feeding.',
  'rules.extinction.title': 'Extinction & New Cards',
  'rules.extinction.text':
    'When feeding is over, the game checks which animals actually survived the round and prepares the next cycle.',
  'rules.extinction.point.1':
    'Unfed animals die and are removed from the table.',
  'rules.extinction.point.2':
    'Surviving animals reset temporary combat states and grow older by one turn.',
  'rules.extinction.point.3':
    'Players draw new cards based on the animals that made it through the round.',
  'rules.extinction.point.4':
    'If a player has no animals left and no cards in hand, they receive a fresh starting hand.',
  'rules.catalogue.title': 'Card Catalogue',
  'rules.catalogue.text':
    'Every current card is shown below with the same visual component used in the game. The short rules text on the card is only the headline; the notes under it explain timing, limits, and what the card is really asking you to do.',
  'rules.group.feedingEconomy.title': 'Feeding & Economy',
  'rules.group.feedingEconomy.intro':
    'These traits help you take food more efficiently, create food pressure, or turn one feeding action into several.',
  'rules.group.predators.title': 'Predators & Hunting',
  'rules.group.predators.intro':
    'These cards shape the hunting game: who can attack, who can break through protection, and how aggressive your table becomes.',
  'rules.group.protection.title': 'Protection & Survival',
  'rules.group.protection.intro':
    'These traits keep animals alive through defence checks, body restrictions, deception, or collateral punishment for attackers.',
  'rules.group.parasites.title': 'Parasites & Sabotage',
  'rules.group.parasites.intro':
    'These hostile traits are placed on enemy animals and are meant to tax, disable, or eventually kill a valuable creature.',
  'rules.group.reproduction.title': 'Reproduction',
  'rules.group.reproduction.intro':
    'These cards turn one fed animal into future board presence. They are powerful long-term investments, but they are not risk-free.',
  'rules.note.foodDemand': ({ value }: FormatterValues) =>
    `Food demand ${value}.`,
  'rules.note.optionalFood': ({ value }: FormatterValues) =>
    `Can also store ${value} extra food.`,
  'rules.note.paired': 'Paired trait.',
  'rules.note.enemy': 'Played onto enemy animals.',
  'rules.note.stackable': 'Can be stacked on the same animal.',
  'rules.note.inheritable': 'May be inherited by offspring.',
  'rules.note.active': 'Has an active ability during feeding.',
  'rules.note.conflicts': ({ conflicts }: FormatterValues) =>
    `Conflicts with ${conflicts}.`,
  'rules.detail.predator.1':
    'A predator replaces normal plant feeding with attacks on animals.',
  'rules.detail.predator.2':
    'To attack, the predator must still be hungry enough to benefit from food.',
  'rules.detail.predator.3':
    'A successful hunt gives the predator 2 food, plus extra from fat stored on the prey.',
  'rules.detail.camouflage.1':
    'Blocks attacks unless the predator has Sharp Vision or enough Intellect to ignore the defence.',
  'rules.detail.camouflage.2':
    'Camouflage is a pure defence card: it does not increase food demand.',
  'rules.detail.camouflage.3':
    'It is inherited by offspring often enough to matter in breeding strategies.',
  'rules.detail.sharpVision.1': 'Lets a predator attack through Camouflage.',
  'rules.detail.sharpVision.2':
    'It only matters in matchups where the prey is protected by Camouflage.',
  'rules.detail.sharpVision.3':
    'Best on dedicated hunters that expect defensive prey instead of raw food races.',
  'rules.detail.swimming.1':
    'Swimming is symmetrical: a swimming animal can only be attacked by a swimming predator, and a swimming predator can only hunt swimming prey.',
  'rules.detail.swimming.2':
    'This effectively creates a separate aquatic lane on the table.',
  'rules.detail.swimming.3':
    'It is strongest when only one player invests in it, because that player controls the entire sea matchup.',
  'rules.detail.running.1':
    'When attacked, the animal gets a defence roll to escape instead of being eaten.',
  'rules.detail.running.2':
    'The card text is the source of truth here: the escape chance is 50% against a normal predator and 10% against a predator that also has Running.',
  'rules.detail.running.3':
    'If the escape succeeds, the predator is blocked for the rest of the turn.',
  'rules.detail.big.1':
    'Large animals can only be attacked by predators that are also Large.',
  'rules.detail.big.2':
    'The trait raises food demand by 1, so it is protection purchased with extra upkeep.',
  'rules.detail.big.3':
    'Large predators are slower to feed, but they unlock prey that smaller hunters cannot touch.',
  'rules.detail.crazing.1':
    'This is an active feeding-stage ability that destroys 1 food from the common pool.',
  'rules.detail.crazing.2':
    'It does not directly feed your animal; it exists to starve the table, deny Communication chains, and pressure greedy builds.',
  'rules.detail.crazing.3':
    'Its value rises when other players rely on plant feeding instead of predation.',
  'rules.detail.poisonous.1':
    'A predator that successfully eats this animal becomes poisoned and dies during extinction.',
  'rules.detail.poisonous.2':
    'Poison does not stop the attack itself; it punishes the predator later.',
  'rules.detail.poisonous.3':
    'Bright Coloration can imitate this card and create bluff pressure even without the real poison effect.',
  'rules.detail.tailLoss.1':
    'Tail Loss always defends successfully when the animal is attacked.',
  'rules.detail.tailLoss.2':
    'The animal survives by discarding one evolution card, sometimes the parasite if one is attached.',
  'rules.detail.tailLoss.3':
    'If the animal is still hungry after losing the card, it immediately gains 1 food from the narrow escape.',
  'rules.detail.hibernation.1':
    'This is an active feeding-stage ability that instantly fills the animal to its normal food requirement.',
  'rules.detail.hibernation.2':
    'It cannot be used in two rounds in a row, because a used Hibernation card remembers the previous stage.',
  'rules.detail.hibernation.3':
    'It cannot be activated in the final round, so it is a tempo tool rather than a guaranteed late-game shield.',
  'rules.detail.scavenger.1':
    'Whenever another animal is eaten, a hungry scavenger gains 1 food.',
  'rules.detail.scavenger.2':
    'That food can trigger feed-based synergies such as Cooperation or reproduction effects.',
  'rules.detail.scavenger.3':
    "Scavenger is strongest in predator-heavy tables because it converts other players' violence into your tempo.",
  'rules.detail.burrowing.1':
    'A burrowing animal becomes untouchable once it is fully fed.',
  'rules.detail.burrowing.2':
    'Until it is full, it is still vulnerable to predators.',
  'rules.detail.burrowing.3':
    'It conflicts with Transparency because both traits are trying to define the same attack window.',
  'rules.detail.piracy.1':
    "This is an active feeding-stage ability that steals 1 food from another player's animal with food already on it.",
  'rules.detail.piracy.2':
    'The victim must still be unfed, so Piracy targets partially-fed animals rather than fully-safe ones.',
  'rules.detail.piracy.3':
    'The stolen food can feed the pirate directly or be converted into stored fat if the base body is already full.',
  'rules.detail.parasite.1': 'Placed on an enemy animal instead of your own.',
  'rules.detail.parasite.2':
    "It increases that animal's food demand by 2, making survival much harder in low-food rounds.",
  'rules.detail.parasite.3':
    'Immunity blocks future parasites and removes existing hostile parasite-like cards.',
  'rules.detail.parasitePair.1':
    'Placed on an enemy pair, so both linked animals carry the card.',
  'rules.detail.parasitePair.2':
    'It increases food demand by 1 on the affected body and also occupies a paired slot.',
  'rules.detail.parasitePair.3':
    'This is especially punishing against Cooperation and Communication engines because it taxes the whole pair structure.',
  'rules.detail.cooperation.1':
    'A paired trait that sits on both animals in the pair.',
  'rules.detail.cooperation.2':
    'Whenever one animal in the pair gets food from any source, the partner also gains 1 food if it can still eat.',
  'rules.detail.cooperation.3':
    'Because the trigger is broad, Cooperation works with plant food, scavenging, piracy, predator rewards, and some chained effects.',
  'rules.detail.communication.1':
    'A paired trait that is stricter than Cooperation.',
  'rules.detail.communication.2':
    'It only triggers when the first animal eats from the common food base, and it spends one more food from that same base to feed the partner out of turn.',
  'rules.detail.communication.3':
    'Communication is explosive when food is plentiful and weak when the table is already starving.',
  'rules.detail.fat.1':
    'Fat Reserve can be stacked multiple times on the same animal.',
  'rules.detail.fat.2':
    'Extra feeding beyond the base requirement is converted into stored fat, one unit per Fat Reserve card.',
  'rules.detail.fat.3':
    'During feeding, stored fat can later be activated and converted back into regular food.',
  'rules.detail.fly.1': 'Flight compares total trait count as a weight test.',
  'rules.detail.fly.2':
    'A flying animal cannot be attacked by a predator with fewer traits than itself.',
  'rules.detail.fly.3':
    'This rewards building a large, layered creature, but that also makes the animal more expensive to feed and more valuable to lose.',
  'rules.detail.angler.1':
    'This card stays hidden until it is revealed by use.',
  'rules.detail.angler.2':
    "If it is the animal's only evolution card and the animal is attacked while the card is still undisclosed, the prey counterattacks instead.",
  'rules.detail.angler.3':
    'The predator dies, the angler gains 2 food, and the card reveals itself afterwards.',
  'rules.detail.pseudoPoisonous.1':
    'Bright Coloration pretends to be Poisonous to opponents.',
  'rules.detail.pseudoPoisonous.2':
    'Other players only see the mimicked face, so the card creates bluff value even though it does not actually poison the predator.',
  'rules.detail.pseudoPoisonous.3':
    'It conflicts with real Poisonous because the whole point is to imitate it, not stack both versions on one body.',
  'rules.detail.viviparous.1':
    'When the parent becomes fully fed, it creates one young animal.',
  'rules.detail.viviparous.2':
    'The offspring may inherit some inheritable traits, but inheritance is intentionally rare.',
  'rules.detail.viviparous.3':
    'The newborn arrives blocked for the round, so Viviparity is an investment in later turns rather than an immediate action spike.',
  'rules.detail.layingEggs.1':
    'When the parent becomes fully fed, it creates several eggs rather than one active child.',
  'rules.detail.layingEggs.2':
    'Eggs wait several rounds before hatching, and the table must keep them safe long enough for the investment to pay off.',
  'rules.detail.layingEggs.3':
    'This is the highest-variance reproduction card: stronger ceiling than Viviparity, but much riskier.',
  'rules.detail.egg.1':
    'Egg is a blocking state placed on unborn offspring rather than a card normally drawn into hand.',
  'rules.detail.egg.2':
    'An egg cannot use traits, cannot defend with normal abilities, and does not function as an active animal until it hatches.',
  'rules.detail.egg.3':
    'In the current ruleset this vulnerability is part of the design: eggs demand protection and board control.',
  'rules.detail.ink.1': 'Ink Cloud is a once-per-round emergency defence.',
  'rules.detail.ink.2':
    'The first time the animal is attacked that round, the attack is interrupted instead of resolving.',
  'rules.detail.ink.3':
    'After the cloud is used, the same card cannot stop another attack until the next round reset.',
  'rules.detail.spikes.1':
    'A predator that eats this animal becomes blocked and cannot attack again that turn.',
  'rules.detail.spikes.2':
    'Unlike Poisonous, Spikes do not kill the predator; they only end the attack chain.',
  'rules.detail.spikes.3':
    'This is a strong anti-snowball tool against predators trying to sweep multiple prey in one round.',
  'rules.detail.scream.1':
    'When this animal is killed, adjacent friendly animals become hidden.',
  'rules.detail.scream.2':
    'That does not feed them or save the victim, but it can shut down follow-up attacks in the same lane.',
  'rules.detail.scream.3':
    'Scream is positional: its value depends heavily on where the animal stands in your lineup.',
  'rules.detail.patron.1':
    'At the start of the round, Patron protects adjacent friendly animals that have no evolution cards at all.',
  'rules.detail.patron.2':
    'That protection is represented separately and can be ignored by Intellect.',
  'rules.detail.patron.3':
    'It is a support card for token-like bodies, hatchlings, and freshly created animals that have not been upgraded yet.',
  'rules.detail.transparent.1':
    'Transparency blocks attacks before the animal has eaten at all.',
  'rules.detail.transparent.2':
    'As soon as the animal has any food on it, the protection is gone for the rest of the round.',
  'rules.detail.transparent.3':
    'This creates a real timing tension: feeding the animal helps it survive extinction, but also reveals it to predators.',
  'rules.detail.horns.1':
    'When the animal is attacked, Horns make a defence roll that can kill the predator outright.',
  'rules.detail.horns.2': 'By card text, the kill chance is 60%.',
  'rules.detail.horns.3':
    'Horns are strongest on prey that are already attractive targets, because they threaten a brutal trade.',
  'rules.detail.aedificator.1':
    'At the start of the feeding round, each copy adds 2 food to the common food pool.',
  'rules.detail.aedificator.2':
    'This helps everyone, not just you, so the card is best when your own build uses food more efficiently than the table does.',
  'rules.detail.aedificator.3':
    'Aedificator is a meta-shaping support trait rather than a personal feeding engine.',
  'rules.detail.immunity.1':
    'Immunity prevents parasite-like hostile traits from being placed on the animal.',
  'rules.detail.immunity.2':
    'When the trait is added, existing Parasite, Trematode, or Tumor effects that conflict with it are removed.',
  'rules.detail.immunity.3':
    'It also clears tumor-based disabling that was already on the animal.',
  'rules.detail.intellect.1':
    'Intellect is a hunting support trait for predators.',
  'rules.detail.intellect.2':
    'Each unused copy can ignore one blocking defence from the prey during an attack, including hidden state, Patron protection, or trait-based attack restrictions.',
  'rules.detail.intellect.3':
    'The balancing cost is steep food demand: multiple Intellect cards scale as +1, then +3, then +5 and so on.',
  'rules.detail.tumor.1': 'Placed on an enemy animal instead of your own.',
  'rules.detail.tumor.2':
    "At the end of each round, Tumor moves deeper through the target's trait stack and disables more traits on the way.",
  'rules.detail.tumor.3':
    'When it reaches the end of the stack, the animal dies.',
  'rules.detail.shell.1':
    'Shell always stops an incoming attack once it is checked as a defence.',
  'rules.detail.shell.2':
    'The animal hides in the shell, cannot eat for the rest of the round, and the predator is blocked from further attacks.',
  'rules.detail.shell.3':
    'When the animal eventually dies, the shell returns to the shared pool as a reusable common card during feeding.',
  'rules.detail.chlorophyll.1':
    "Photosynthesis reduces the animal's food requirement by 1.",
  'rules.detail.chlorophyll.2':
    'It conflicts with Predator, Cooperation, and Communication, so it pushes the body toward a self-sufficient herbivore role.',
  'rules.detail.chlorophyll.3':
    'Because it directly lowers upkeep, it also lowers how much food you must secure to keep the animal alive.',
  'admin.action.oneStepBack': 'One Step Back',
  'admin.action.toStageStart': 'To Stage Start',
  'admin.action.toCycleStart': 'To Cycle Start',
  'admin.toast.actionSent': 'Action request "{label}" sent',
  'devtools.title': 'DevTools',
  'devtools.addRandomAnimal': 'add random animal',
  'devtools.addFood': ({ count }: FormatterValues) => `+ ${count} food`,
  'devtools.removeFood': ({ count }: FormatterValues) => `- ${count} food`,
  'devtools.deleteAllFood': 'delete all food',
  'results.mostIncomingParasites':
    'Most parasites received: {playerName} ({score})',
  'results.mostOutgoingParasites':
    'Most parasites placed: {playerName} ({score})',
  'results.mostPredatorAttacks':
    'Most predator attacks: {playerName} ({score})',
  'results.mostExtensionsUsed':
    'Most evolution cards used: {playerName} ({score})',
  'log.actionProcessingError': 'An error occurred while processing the action',
  'log.playerAddedCardToHand': '{playerName} added {cardName} to their hand.',
  'log.someoneAddedFood': ({ playerName, count }: FormatterValues) =>
    `${playerName} added ${count} ${Number(count) === 1 ? 'food' : 'food'}.`,
  'log.tramplingDestroysFood': 'Trampling destroys one food',
  'log.animalEscaped': 'Your animal escaped from a predator',
  'log.animalScavenged': 'Your animal scavenged after a predator',
  'log.discardedExtension': 'Your animal discarded "{cardName}"',
  'log.foodStolen': 'Food was stolen from your animal by {playerName}',
  'log.inkCloud': 'Your animal used an ink cloud',
  'log.hungerDeaths': ({ count }: FormatterValues) =>
    `${formatCount('en', Number(count), {
      one: 'animal',
      other: 'animals',
    })} died of hunger`,
  'log.cardsForSurvivors': ({ cardsAdded, survivors }: FormatterValues) =>
    `Added ${formatCount('en', Number(cardsAdded), {
      one: 'card',
      other: 'cards',
    })} for ${formatCount('en', Number(survivors), {
      one: 'surviving animal',
      other: 'surviving animals',
    })}`,
  'log.noAnimalsStartingHand':
    'No animals survived and you have no cards, added {count}',
  'log.animalDiesFromPoison': 'Your animal dies from poison',
  'log.extensionPlacedOnYourAnimal':
    '{playerName} placed "{cardName}" on your animal',
  'log.predatorDied': 'The predator died during the attack',
  'log.animalWasEaten': ({ animalName, playerName }: FormatterValues) =>
    `Your animal${
      animalName ? ` "${animalName}"` : ''
    } was eaten by ${playerName}'s predator`,
} as const;

export type GeneralMessageKey = keyof typeof generalMessagesEn;

const generalMessagesRu: Record<GeneralMessageKey, GeneralMessageEntry> = {
  'app.name': 'Эволюция',
  'app.title': ({ version }: FormatterValues) => `Эволюция v${version}`,
  'header.leave': 'Выйти',
  'header.save': 'Сохранить',
  'header.editLayout': 'Редактировать раскладку',
  'header.reset': 'Сбросить',
  'header.fullScreen': 'На весь экран',
  'header.admin': 'Админ',
  'header.game': 'Игра',
  'header.language.ru': 'RU',
  'header.language.en': 'EN',
  'footer.home': 'Главная',
  'footer.rules': 'Правила',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'common.ok': 'OK',
  'common.you': 'Вы',
  'player.unnamed': 'Без имени',
  'player.someone': 'Кто-то',
  'player.another': 'другой игрок',
  'animal.fallback': 'Животное',
  'animal.meta.age': ({ count }: FormatterValues) =>
    formatCount('ru', Number(count), {
      one: 'год',
      few: 'года',
      many: 'лет',
      other: 'лет',
    }),
  'animal.meta.cantAttack': 'не может атаковать',
  'animal.meta.poisoned': 'отравлено',
  'animal.meta.hidden': 'скрыто',
  'animal.meta.cantEat': 'не может есть',
  'animal.meta.protected': 'защищено',
  'animal.meta.full': 'сыто',
  'animal.meta.inBurrow': 'в норе',
  'animal.meta.kg': ({ count }: FormatterValues) => `${count} кг`,
  'animal.meta.hatchesIn': ({ count }: FormatterValues) =>
    `вылупится через ${formatCount('ru', Number(count), {
      one: 'ход',
      few: 'хода',
      many: 'ходов',
      other: 'хода',
    })}`,
  'animals.youTag': 'Вы',
  'animals.ready': 'Готов',
  'ready.ready': 'Готов',
  'ready.notReady': 'Не готов',
  'hud.stage': 'Этап',
  'hud.active': 'Ход',
  'hud.remaining': 'Осталось',
  'hud.finalTurn': 'Последний ход',
  'hud.turnsApprox': ({ count }: FormatterValues) =>
    `~${formatCount('ru', Number(count), {
      one: 'ход',
      few: 'хода',
      many: 'ходов',
      other: 'хода',
    })}`,
  'hud.minutesApprox': ({ count }: FormatterValues) =>
    `~${formatCount('ru', Number(count), {
      one: 'минута',
      few: 'минуты',
      many: 'минут',
      other: 'минуты',
    })}`,
  'splash.starting': 'Начинается',
  'fsloader.waiting': 'Ожидание соединения...',
  'fsloader.retry': 'Попробовать снова',
  'mobile.title': 'Мобильная версия',
  'mobile.description.1':
    'Игра пока не оптимизирована для мобильных устройств.',
  'mobile.description.2': 'Нажмите "OK", чтобы перейти к основному интерфейсу.',
  'confirm.attackOwnAnimal': 'Это ваше животное. Точно продолжить?',
  'name.label': 'Имя',
  'name.example': ({ example }: FormatterValues) => `Например, ${example}`,
  'waiting.avatar': 'Аватар',
  'waiting.gameSettings': 'Настройки игры',
  'waiting.personalSettings': 'Личные настройки',
  'waiting.startGame': 'Начать игру',
  'waiting.players': 'Игроки:',
  'waiting.youSuffix': '(Вы)',
  'waiting.linkCopied': 'Ссылка скопирована!',
  'waiting.modal.settings.title': 'Настройки',
  'waiting.modal.settings.initialCards': 'Начальных карт на игрока:',
  'waiting.modal.settings.cardsPerSurvivor': 'Карт за выжившее животное:',
  'waiting.modal.settings.totalCards': 'Всего карт в колоде:',
  'waiting.modal.settings.foodFrom': 'Еда от',
  'waiting.modal.settings.foodTo': 'до',
  'waiting.modal.settings.deckSettings': 'Настройки колоды',
  'waiting.modal.settings.reset': 'Сбросить настройки',
  'waiting.modal.settings.selectAll': 'Выбрать всё',
  'waiting.modal.settings.card': 'Карта',
  'waiting.modal.personal.title': 'Личные настройки',
  'results.player': 'Игрок',
  'results.score': 'Очки',
  'results.additional': 'Дополнительные результаты:',
  'results.startNewGame': 'Начать новую игру',
  'extension.use': 'Использовать',
  'extension.giftFrom': 'Подарок от {playerName}',
  'hand.preview.title': 'Карта',
  'hand.preview.body': 'Я карта',
  'start.eyebrow': 'Онлайн-игра про выживание видов',
  'start.title':
    'Собирайте виды, которые смогут выжить, прокормиться и пережить соперников.',
  'start.description':
    'Создавайте животных, развивайте свойства, боритесь за еду и наказывайте слабые адаптации. Побеждает сильнейшая экосистема.',
  'start.howToStart': 'Как начать',
  'start.whatHappensNext': 'Что дальше',
  'start.quickRules': 'Коротко о правилах',
  'start.readFullRules': 'Открыть полные правила',
  'start.rulesNote': 'или начните сразу и разбирайтесь по справочнику карт.',
  'start.form.title': 'Войти в комнату или создать новую',
  'start.form.description':
    'Используйте тот же код комнаты, что и друзья, чтобы попасть в одну игру.',
  'start.roomCode': 'Код комнаты',
  'start.roomExample': ({ example }: FormatterValues) => `Например, ${example}`,
  'start.roomHelp': 'Одинаковые коды всегда ведут в одну и ту же комнату.',
  'start.publicRoom': 'Публичная комната',
  'start.publicRoomHelp':
    'Публичные комнаты появляются в списке открытых комнат, чтобы к ним могли присоединиться другие игроки.',
  'start.nameHelp': 'Отображаемое имя можно будет поменять в лобби.',
  'start.play': 'Играть',
  'start.playHint.ready': 'Нажмите «Играть», чтобы войти в лобби этой комнаты.',
  'start.playHint.empty':
    'Введите код комнаты, чтобы создать новую или присоединиться к существующей.',
  'start.openRooms.title': 'Присоединиться к открытой публичной комнате',
  'start.openRooms.description':
    'Выберите комнату ниже, если хотите быстро зайти в активное лобби.',
  'start.openRooms.players': ({ count }: FormatterValues) => `Игроки: ${count}`,
  'start.step.1':
    'Введите код комнаты, чтобы присоединиться к друзьям или создать новый стол.',
  'start.step.2': 'Выберите имя, которое увидят остальные игроки.',
  'start.step.3':
    'Нажмите «Играть», чтобы войти в лобби и подготовиться к матчу.',
  'start.next.1': 'Настройте аватар и личные параметры в лобби.',
  'start.next.2':
    'Пригласите друзей по ссылке на комнату или дождитесь, пока они подключатся.',
  'start.next.3': 'Запускайте игру, когда все будут готовы.',
  'start.rule.1':
    'Еда определяет каждый раунд, поэтому голодные животные всегда под давлением.',
  'start.rule.2':
    'Свойства помогают вашему виду быстрее кормиться, защищаться или становиться хищником.',
  'start.rule.3':
    'Побеждает игрок, чьи животные лучше всего адаптируются со временем.',
  'rules.pageTitle': 'Правила игры',
  'rules.hero.eyebrow': 'Обзор',
  'rules.hero.title': 'Стройте, кормите, выживайте',
  'rules.hero.text':
    'Развивайте линию животных, усиливайте их свойствами и старайтесь сохранить больше животных, чем остальные. Важны сильные комбинации, но не меньшее значение имеет тайминг: еды мало, хищники опасны, а каждое выжившее животное приносит очки.',
  'rules.goal.title': 'Цель и подсчёт очков',
  'rules.goal.text':
    'Игра заканчивается, когда колода иссякает и завершается финальная фаза вымирания. Очки получают только выжившие животные.',
  'rules.goal.point.1': 'Каждое выжившее животное стоит 2 очка.',
  'rules.goal.point.2': 'Каждое выжившее свойство стоит 1 очко.',
  'rules.goal.point.3':
    'Свойства, меняющие потребность в еде, настолько же меняют и свою ценность в очках. Например, Хищник и Крупное стоят как свойства по 2 очка, а Паразит — 3.',
  'rules.cards.title': 'Как работают карты',
  'rules.cards.text':
    'Каждую карту эволюции можно использовать двумя способами: начать с неё новое животное или прикрепить её как свойство к уже существующему животному на столе.',
  'rules.cards.point.1':
    'Большинство свойств не могут повторяться на одном животном. Заметные исключения — Жировой запас и Интеллект: их можно накапливать.',
  'rules.cards.point.2':
    'Парные свойства связывают двух животных и лежат с обеих сторон этой пары.',
  'rules.cards.point.3':
    'Некоторые враждебные свойства, например Паразит, Трематода и Опухоль, кладутся на животных соперника, а не на ваших собственных.',
  'rules.cards.point.4':
    'Некоторые свойства конфликтуют друг с другом, поэтому не каждое сочетание разрешено.',
  'rules.placing.title': 'Розыгрыш карт',
  'rules.placing.text':
    'На этапе розыгрыша карт игроки собирают свой стол: создают новых животных и добавляют свойства. Этап продолжается, пока каждый игрок не закончит и не отметит себя готовым.',
  'rules.placing.point.1':
    'Пока этап розыгрыша открыт, вы можете продолжать выкладывать карты.',
  'rules.placing.point.2':
    'Карта, использованная для создания животного, уходит из руки и становится базой нового вида.',
  'rules.placing.point.3':
    'Карта, сыгранная как свойство, прикрепляется к существующему животному или к допустимой паре животных, если свойство парное.',
  'rules.feeding.title': 'Кормление',
  'rules.feeding.text':
    'Кормление происходит по очереди. Активный игрок выполняет одно полезное действие кормления, затем приоритет переходит следующему игроку, который ещё не закончил.',
  'rules.feeding.point.1':
    'Обычное действие кормления берёт 1 еду из общей кормовой базы и кладёт её на одно из ваших животных.',
  'rules.feeding.point.2':
    'Сытое животное уже не может брать обычную еду, но излишек всё ещё может заполнить Жировой запас.',
  'rules.feeding.point.3':
    'Атака хищника заменяет обычное кормление на это действие. Если жертва не смогла защититься, хищник получает еду, а другие свойства могут сработать от убийства.',
  'rules.feeding.point.4':
    'Сотрудничество кормит парное животное каждый раз, когда первое получает еду. Коммуникация строже: она срабатывает только если первое животное ело из общей базы и тратит ещё одну еду из той же базы.',
  'rules.feeding.point.5':
    'Падальщик получает еду, когда съедают другое животное, а накопленный жир можно позже превратить в обычную еду во время кормления.',
  'rules.extinction.title': 'Вымирание и новые карты',
  'rules.extinction.text':
    'Когда кормление заканчивается, игра проверяет, какие животные действительно пережили раунд, и готовит следующий цикл.',
  'rules.extinction.point.1':
    'Ненакормленные животные погибают и убираются со стола.',
  'rules.extinction.point.2':
    'Выжившие животные сбрасывают временные боевые состояния и стареют на один ход.',
  'rules.extinction.point.3':
    'Игроки добирают новые карты в зависимости от того, сколько животных пережило раунд.',
  'rules.extinction.point.4':
    'Если у игрока не осталось животных и карт в руке, он получает новую стартовую руку.',
  'rules.catalogue.title': 'Каталог карт',
  'rules.catalogue.text':
    'Ниже показаны все текущие карты в том же визуальном виде, что и в игре. Короткий текст на карте — это только заголовок; заметки под ней поясняют тайминг, ограничения и реальную роль карты.',
  'rules.group.feedingEconomy.title': 'Кормление и экономика',
  'rules.group.feedingEconomy.intro':
    'Эти свойства помогают эффективнее получать еду, создавать дефицит еды или превращать одно действие кормления в несколько.',
  'rules.group.predators.title': 'Хищники и охота',
  'rules.group.predators.intro':
    'Эти карты задают динамику охоты: кто может нападать, кто пробивает защиту и насколько агрессивным становится стол.',
  'rules.group.protection.title': 'Защита и выживание',
  'rules.group.protection.intro':
    'Эти свойства удерживают животных в живых за счёт проверок защиты, ограничений по телу, обмана или наказания для атакующего.',
  'rules.group.parasites.title': 'Паразиты и саботаж',
  'rules.group.parasites.intro':
    'Эти враждебные свойства кладутся на животных соперника и должны ослаблять, отключать или со временем убивать ценную цель.',
  'rules.group.reproduction.title': 'Размножение',
  'rules.group.reproduction.intro':
    'Эти карты превращают одно сытое животное в будущую позицию на столе. Это сильная долгосрочная инвестиция, но без гарантий.',
  'rules.note.foodDemand': ({ value }: FormatterValues) =>
    `Потребность в еде ${value}.`,
  'rules.note.optionalFood': ({ value }: FormatterValues) =>
    `Может дополнительно хранить ${value} еды.`,
  'rules.note.paired': 'Парное свойство.',
  'rules.note.enemy': 'Играется на животных соперника.',
  'rules.note.stackable': 'Можно складывать на одном животном.',
  'rules.note.inheritable': 'Может наследоваться потомством.',
  'rules.note.active': 'Имеет активную способность во время кормления.',
  'rules.note.conflicts': ({ conflicts }: FormatterValues) =>
    `Конфликтует с ${conflicts}.`,
  'rules.detail.predator.1':
    'Хищник заменяет обычное растительное кормление атаками на животных.',
  'rules.detail.predator.2':
    'Чтобы атаковать, хищник всё ещё должен быть голоден настолько, чтобы получить пользу от еды.',
  'rules.detail.predator.3':
    'Успешная охота даёт хищнику 2 еды, плюс дополнительную еду из жира, накопленного на жертве.',
  'rules.detail.camouflage.1':
    'Блокирует атаки, если у хищника нет Острого зрения или достаточного Интеллекта, чтобы игнорировать защиту.',
  'rules.detail.camouflage.2':
    'Камуфляж — чисто защитная карта: она не увеличивает потребность в еде.',
  'rules.detail.camouflage.3':
    'Она наследуется потомством достаточно часто, чтобы иметь значение в стратегиях размножения.',
  'rules.detail.sharpVision.1': 'Позволяет хищнику атаковать сквозь Камуфляж.',
  'rules.detail.sharpVision.2':
    'Имеет значение только в матчапах, где добыча защищена Камуфляжем.',
  'rules.detail.sharpVision.3':
    'Лучше всего работает на специализированных охотниках, которые ожидают защищённую добычу, а не голую гонку за едой.',
  'rules.detail.swimming.1':
    'Водоплавающее симметрично: водоплавающее животное может быть атаковано только водоплавающим хищником, а водоплавающий хищник может охотиться только на водоплавающую добычу.',
  'rules.detail.swimming.2':
    'Фактически это создаёт отдельную водную линию на столе.',
  'rules.detail.swimming.3':
    'Свойство особенно сильно, когда в него вкладывается только один игрок, потому что он получает полный контроль над морским матчапом.',
  'rules.detail.running.1':
    'При атаке животное получает защитный бросок на побег вместо того, чтобы быть съеденным.',
  'rules.detail.running.2':
    'Текст карты здесь — источник истины: шанс побега 50% против обычного хищника и 10% против хищника, у которого тоже есть Бег.',
  'rules.detail.running.3':
    'Если побег успешен, хищник блокируется до конца хода.',
  'rules.detail.big.1':
    'Крупных животных могут атаковать только крупные хищники.',
  'rules.detail.big.2':
    'Свойство увеличивает потребность в еде на 1, то есть защита покупается за дополнительное содержание.',
  'rules.detail.big.3':
    'Крупные хищники медленнее насыщаются, зато открывают добычу, до которой не доберутся мелкие охотники.',
  'rules.detail.crazing.1':
    'Это активная способность этапа кормления, которая уничтожает 1 еду из общего пула.',
  'rules.detail.crazing.2':
    'Она не кормит животное напрямую; её задача — морить стол голодом, ломать цепочки Коммуникации и давить жадные сборки.',
  'rules.detail.crazing.3':
    'Её ценность растёт, когда остальные игроки зависят от растительного кормления, а не от хищничества.',
  'rules.detail.poisonous.1':
    'Хищник, успешно съевший это животное, становится отравленным и погибает во время вымирания.',
  'rules.detail.poisonous.2':
    'Яд не останавливает саму атаку; он наказывает хищника позже.',
  'rules.detail.poisonous.3':
    'Яркая окраска может имитировать эту карту и создавать блеф даже без настоящего эффекта яда.',
  'rules.detail.tailLoss.1':
    'Отбрасывание хвоста всегда успешно защищает животное при атаке.',
  'rules.detail.tailLoss.2':
    'Животное выживает, сбрасывая одну карту эволюции, иногда даже паразита, если он был прикреплён.',
  'rules.detail.tailLoss.3':
    'Если после потери карты животное всё ещё голодно, оно немедленно получает 1 еду за спасение.',
  'rules.detail.hibernation.1':
    'Это активная способность этапа кормления, которая мгновенно заполняет обычную потребность животного в еде.',
  'rules.detail.hibernation.2':
    'Её нельзя использовать два раунда подряд, потому что использованная карта Спячки помнит предыдущий этап.',
  'rules.detail.hibernation.3':
    'Её нельзя активировать в финальном раунде, так что это инструмент темпа, а не гарантированный поздний щит.',
  'rules.detail.scavenger.1':
    'Каждый раз, когда другое животное съедают, голодный Падальщик получает 1 еду.',
  'rules.detail.scavenger.2':
    'Эта еда может запускать синергии от кормления, например Сотрудничество или эффекты размножения.',
  'rules.detail.scavenger.3':
    'Падальщик особенно силён за столами с большим числом хищников, потому что превращает чужое насилие в ваш темп.',
  'rules.detail.burrowing.1':
    'Норное животное становится недосягаемым, как только полностью насытится.',
  'rules.detail.burrowing.2':
    'Пока оно не сыто, оно всё ещё уязвимо для хищников.',
  'rules.detail.burrowing.3':
    'Оно конфликтует с Прозрачностью, потому что оба свойства пытаются определить одно и то же окно для атаки.',
  'rules.detail.piracy.1':
    'Это активная способность этапа кормления, которая крадёт 1 еду у животного другого игрока, если на нём уже есть еда.',
  'rules.detail.piracy.2':
    'Жертва всё ещё должна оставаться ненакормленной, поэтому Пиратство бьёт по частично накормленным животным, а не по уже безопасным.',
  'rules.detail.piracy.3':
    'Украденная еда может напрямую накормить пирата или превратиться в запас жира, если базовое тело уже сыто.',
  'rules.detail.parasite.1': 'Кладётся на вражеское животное, а не на своё.',
  'rules.detail.parasite.2':
    'Увеличивает потребность этого животного в еде на 2, резко усложняя выживание в раундах с нехваткой еды.',
  'rules.detail.parasite.3':
    'Иммунитет блокирует новых паразитов и снимает уже существующие враждебные паразитоподобные карты.',
  'rules.detail.parasitePair.1':
    'Кладётся на вражескую пару, так что карту носят оба связанных животных.',
  'rules.detail.parasitePair.2':
    'Она увеличивает потребность в еде на 1 у поражённого тела и занимает парный слот.',
  'rules.detail.parasitePair.3':
    'Это особенно болезненно против движков на Сотрудничестве и Коммуникации, потому что налог ложится на всю структуру пары.',
  'rules.detail.cooperation.1':
    'Парное свойство, которое лежит на обоих животных в паре.',
  'rules.detail.cooperation.2':
    'Каждый раз, когда одно животное в паре получает еду из любого источника, партнёр тоже получает 1 еду, если ещё может есть.',
  'rules.detail.cooperation.3':
    'Поскольку триггер широкий, Сотрудничество работает с растительной едой, падалью, пиратством, наградой хищника и некоторыми цепочками эффектов.',
  'rules.detail.communication.1':
    'Парное свойство, более строгое, чем Сотрудничество.',
  'rules.detail.communication.2':
    'Оно срабатывает только когда первое животное ест из общей кормовой базы, и тратит ещё одну еду из той же базы, чтобы накормить партнёра вне очереди.',
  'rules.detail.communication.3':
    'Коммуникация взрывоопасно сильна при обилии еды и слаба, когда стол уже голодает.',
  'rules.detail.fat.1':
    'Жировой запас можно складывать на одном животном несколько раз.',
  'rules.detail.fat.2':
    'Избыточное кормление сверх базовой потребности превращается в запас жира, по одной единице на каждую карту Жирового запаса.',
  'rules.detail.fat.3':
    'Во время кормления запасённый жир позже можно активировать и превратить обратно в обычную еду.',
  'rules.detail.fly.1':
    'Полёт сравнивает общее число свойств как проверку «веса».',
  'rules.detail.fly.2':
    'Летающее животное не может быть атаковано хищником, у которого меньше свойств, чем у него самого.',
  'rules.detail.fly.3':
    'Это поощряет строить большое многослойное существо, но такое животное и кормить дороже, и потерять больнее.',
  'rules.detail.angler.1':
    'Эта карта остаётся скрытой, пока не раскроется при использовании.',
  'rules.detail.angler.2':
    'Если это единственная карта эволюции животного и на него нападают, пока карта ещё не раскрыта, добыча контратакует вместо обычной защиты.',
  'rules.detail.angler.3':
    'Хищник погибает, удильщик получает 2 еды, а карта после этого раскрывается.',
  'rules.detail.pseudoPoisonous.1':
    'Яркая окраска притворяется Ядовитым свойством для соперников.',
  'rules.detail.pseudoPoisonous.2':
    'Другие игроки видят только имитируемую сторону, поэтому карта создаёт ценность блефа, даже хотя на самом деле не отравляет хищника.',
  'rules.detail.pseudoPoisonous.3':
    'Она конфликтует с настоящим Ядовитым, потому что смысл карты — имитация, а не суммирование обеих версий на одном теле.',
  'rules.detail.viviparous.1':
    'Когда родитель полностью насыщается, он создаёт одно молодое животное.',
  'rules.detail.viviparous.2':
    'Потомок может унаследовать некоторые наследуемые свойства, но наследование намеренно редкое.',
  'rules.detail.viviparous.3':
    'Новорождённый приходит заблокированным до конца раунда, так что Живорождение — инвестиция в следующие ходы, а не мгновенный всплеск действия.',
  'rules.detail.layingEggs.1':
    'Когда родитель полностью насыщается, он создаёт несколько яиц, а не одного активного ребёнка.',
  'rules.detail.layingEggs.2':
    'Яйца ждут несколько раундов до вылупления, и стол должен удержать их в безопасности достаточно долго, чтобы инвестиция окупилась.',
  'rules.detail.layingEggs.3':
    'Это карта размножения с самой высокой дисперсией: потолок сильнее, чем у Живорождения, но и риск намного выше.',
  'rules.detail.egg.1':
    'Яйцо — это блокирующее состояние нерождённого потомства, а не обычная карта, которую тянут в руку.',
  'rules.detail.egg.2':
    'Яйцо не может использовать свойства, защищаться обычными способностями и не считается активным животным, пока не вылупится.',
  'rules.detail.egg.3':
    'В текущем наборе правил эта уязвимость задумана специально: яйца требуют защиты и контроля стола.',
  'rules.detail.ink.1':
    'Чернильное облако — экстренная защита один раз за раунд.',
  'rules.detail.ink.2':
    'Первый раз, когда это животное атакуют в раунде, атака прерывается вместо обычного разрешения.',
  'rules.detail.ink.3':
    'После использования облака та же карта не может остановить ещё одну атаку до следующего сброса раунда.',
  'rules.detail.spikes.1':
    'Хищник, съевший это животное, блокируется и уже не может атаковать в этом ходу.',
  'rules.detail.spikes.2':
    'В отличие от Ядовитого, Шипы не убивают хищника, а только обрывают цепочку атак.',
  'rules.detail.spikes.3':
    'Это сильный анти-снежный инструмент против хищников, которые пытаются зачистить несколько жертв за раунд.',
  'rules.detail.scream.1':
    'Когда это животное погибает, соседние союзные животные становятся скрытыми.',
  'rules.detail.scream.2':
    'Это не кормит их и не спасает жертву, но может остановить последующие атаки в той же линии.',
  'rules.detail.scream.3':
    'Крик — позиционное свойство: его ценность сильно зависит от места животного в вашей линии.',
  'rules.detail.patron.1':
    'В начале раунда Покровитель защищает соседних союзных животных, у которых вообще нет карт эволюции.',
  'rules.detail.patron.2':
    'Эта защита представлена отдельно и может быть проигнорирована Интеллектом.',
  'rules.detail.patron.3':
    'Это поддерживающая карта для токеноподобных тел, вылупившихся существ и недавно созданных животных без улучшений.',
  'rules.detail.transparent.1':
    'Прозрачность блокирует атаки до того, как животное вообще съело что-либо.',
  'rules.detail.transparent.2':
    'Как только на животном появляется хоть немного еды, защита исчезает до конца раунда.',
  'rules.detail.transparent.3':
    'Это создаёт настоящее напряжение по таймингу: кормить животное полезно для выживания, но это же открывает его для хищников.',
  'rules.detail.horns.1':
    'Когда животное атакуют, Рога дают защитный бросок, который может сразу убить хищника.',
  'rules.detail.horns.2': 'Согласно тексту карты, шанс убийства равен 60%.',
  'rules.detail.horns.3':
    'Рога особенно сильны на добыче, которая и так привлекает атаки, потому что угрожают крайне выгодным разменом.',
  'rules.detail.aedificator.1':
    'В начале этапа кормления каждая копия добавляет 2 еды в общую кормовую базу.',
  'rules.detail.aedificator.2':
    'Это помогает всем, а не только вам, поэтому карта сильнее всего, когда ваша сборка использует еду эффективнее остальных.',
  'rules.detail.aedificator.3':
    'Эдификатор — свойство поддержки, меняющее мету стола, а не личный двигатель кормления.',
  'rules.detail.immunity.1':
    'Иммунитет не даёт класть на животное паразитоподобные враждебные свойства.',
  'rules.detail.immunity.2':
    'Когда свойство добавляется, уже существующие Паразит, Трематода или Опухоль, конфликтующие с ним, удаляются.',
  'rules.detail.immunity.3':
    'Оно также снимает уже накопившееся отключение свойств от опухоли.',
  'rules.detail.intellect.1': 'Интеллект — охотничья поддержка для хищников.',
  'rules.detail.intellect.2':
    'Каждая неиспользованная копия может игнорировать одну блокирующую защиту добычи при атаке, включая скрытность, защиту Покровителя или ограничения по свойствам.',
  'rules.detail.intellect.3':
    'Балансирующая цена высока: несколько карт Интеллекта увеличивают потребность в еде как +1, затем +3, затем +5 и так далее.',
  'rules.detail.tumor.1': 'Кладётся на вражеское животное, а не на своё.',
  'rules.detail.tumor.2':
    'В конце каждого раунда Опухоль продвигается глубже по стопке свойств цели и по дороге отключает всё больше свойств.',
  'rules.detail.tumor.3':
    'Когда она доходит до конца стопки, животное погибает.',
  'rules.detail.shell.1':
    'Панцирь всегда останавливает входящую атаку в тот момент, когда проверяется как защита.',
  'rules.detail.shell.2':
    'Животное прячется в панцирь, не может есть до конца раунда, а хищник блокируется и не может продолжать атаки.',
  'rules.detail.shell.3':
    'Когда животное всё же погибает, панцирь возвращается в общий пул и снова используется как общая карта во время кормления.',
  'rules.detail.chlorophyll.1':
    'Фотосинтез уменьшает потребность животного в еде на 1.',
  'rules.detail.chlorophyll.2':
    'Он конфликтует с Хищником, Сотрудничеством и Коммуникацией, поэтому толкает тело к самостоятельной травоядной роли.',
  'rules.detail.chlorophyll.3':
    'Поскольку он напрямую снижает содержание, он одновременно уменьшает и объём еды, который нужно добыть, чтобы животное выжило.',
  'admin.action.oneStepBack': 'Шаг назад',
  'admin.action.toStageStart': 'К началу этапа',
  'admin.action.toCycleStart': 'К началу цикла',
  'admin.toast.actionSent': 'Запрос действия "{label}" отправлен',
  'devtools.title': 'DevTools',
  'devtools.addRandomAnimal': 'добавить случайное животное',
  'devtools.addFood': ({ count }: FormatterValues) => `+ ${count} еды`,
  'devtools.removeFood': ({ count }: FormatterValues) => `- ${count} еды`,
  'devtools.deleteAllFood': 'удалить всю еду',
  'results.mostIncomingParasites':
    'Больше всего паразитов получено: {playerName} ({score})',
  'results.mostOutgoingParasites':
    'Больше всего паразитов размещено: {playerName} ({score})',
  'results.mostPredatorAttacks':
    'Больше всего атак хищником: {playerName} ({score})',
  'results.mostExtensionsUsed':
    'Больше всего карт эволюции использовано: {playerName} ({score})',
  'log.actionProcessingError': 'Во время обработки действия произошла ошибка',
  'log.playerAddedCardToHand': '{playerName} добавил(а) {cardName} в руку.',
  'log.someoneAddedFood': ({ playerName, count }: FormatterValues) =>
    `${playerName} добавил(а) ${count} еды.`,
  'log.tramplingDestroysFood': 'Вытаптывание уничтожает 1 еду',
  'log.animalEscaped': 'Ваше животное убежало от хищника',
  'log.animalScavenged': 'Ваше животное подобрало пищу после атаки хищника',
  'log.discardedExtension': 'Ваше животное сбросило "{cardName}"',
  'log.foodStolen': 'Еду у вашего животного украл {playerName}',
  'log.inkCloud': 'Ваше животное использовало чернильное облако',
  'log.hungerDeaths': ({ count }: FormatterValues) =>
    `${formatCount('ru', Number(count), {
      one: 'животное',
      few: 'животных',
      many: 'животных',
      other: 'животных',
    })} погибло от голода`,
  'log.cardsForSurvivors': ({ cardsAdded, survivors }: FormatterValues) =>
    `Добавлено ${formatCount('ru', Number(cardsAdded), {
      one: 'карта',
      few: 'карты',
      many: 'карт',
      other: 'карты',
    })} за ${formatCount('ru', Number(survivors), {
      one: 'выжившее животное',
      few: 'выживших животных',
      many: 'выживших животных',
      other: 'выживших животных',
    })}`,
  'log.noAnimalsStartingHand':
    'Не выжило ни одного животного и у вас нет карт, добавлено {count}',
  'log.animalDiesFromPoison': 'Ваше животное погибает от яда',
  'log.extensionPlacedOnYourAnimal':
    '{playerName} положил(а) "{cardName}" на ваше животное',
  'log.predatorDied': 'Хищник погиб во время атаки',
  'log.animalWasEaten': ({ animalName, playerName }: FormatterValues) =>
    `Ваше животное${
      animalName ? ` "${animalName}"` : ''
    } было съедено хищником игрока ${playerName}`,
};

export type MessageKey =
  | GeneralMessageKey
  | CardMessageKey
  | StageMessageKey
  | HandTypeMessageKey
  | GeneratedAnimalNameMessageKey;

export type MessageDescriptor = BaseMessageDescriptor<MessageKey>;
export type TranslatableText = string | MessageDescriptor;

export const message = <Key extends MessageKey>(
  key: Key,
  values?: MessageValues<MessageKey>,
): MessageDescriptor => ({
  key,
  values,
});

export const cardMessageKey = (
  type: ExtensionType,
  field: 'name' | 'description' | 'shortName',
): CardMessageKey => `card.${type}.${field}` as CardMessageKey;

export const stageMessageKey = (stage: GameStage): StageMessageKey =>
  `stage.${stage}` as StageMessageKey;

export const handTypeMessageKey = (type: HandType): HandTypeMessageKey =>
  `handType.${type}` as HandTypeMessageKey;

export const generatedAnimalNameMessageKey = (
  name: GeneratedAnimalName,
): GeneratedAnimalNameMessageKey =>
  `animal.generated.${name}` as GeneratedAnimalNameMessageKey;

export const getGeneralMessages = (
  locale: 'en' | 'ru',
): Record<GeneralMessageKey, GeneralMessageEntry> =>
  locale === 'ru' ? generalMessagesRu : generalMessagesEn;
